import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE_URL as API } from '../config/api';

const Waitlist = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/waitlist/`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Success!", {
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: ''
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error("Error", {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="py-20 bg-gradient-to-br from-[#40E095]/5 via-white to-emerald-50/30 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#40E095]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#40E095]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-600">
              Join the waitlist and be among the first to experience secure cloud storage
            </p>
          </div>

          {/* Form Card */}
          <Card className="p-8 md:p-12 shadow-2xl border-2 border-gray-100">
            {isSubmitted ? (
              <div className="text-center py-12 space-y-6">
                <div className="bg-[#40E095]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="text-[#40E095]" size={48} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">You're on the list!</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Thank you for joining our waitlist. We'll send you an invite soon. 
                  Check your email for next steps.
                </p>
                <Button 
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-2 border-[#40E095] text-[#40E095] hover:bg-[#40E095] hover:text-white"
                >
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700 font-medium">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-2 border-gray-200 focus:border-[#40E095] transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-2 border-gray-200 focus:border-[#40E095] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-700 font-medium">
                      Company (Optional)
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      placeholder="Your Company"
                      value={formData.company}
                      onChange={handleChange}
                      className="border-2 border-gray-200 focus:border-[#40E095] transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700 font-medium">
                      Phone Number (Optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      className="border-2 border-gray-200 focus:border-[#40E095] transition-colors"
                    />
                  </div>
                </div>

                <div className="bg-[#40E095]/5 rounded-xl p-4 border border-[#40E095]/20">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    By joining the waitlist, you'll get early access to Drivault with 100GB free storage 
                    and the ability to earn up to 1TB through referrals.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#40E095] hover:bg-[#36C981] text-white font-semibold py-6 text-lg transition-all hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Submitting...
                    </>
                  ) : (
                    'Request Your Invite'
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  We respect your privacy. Your information will never be shared with third parties.
                </p>
              </form>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Waitlist;
